import { SortDirectionEnum } from "enums/SortDirectionEnum";

export const compareNumbers = (
  valueA: number | undefined,
  valueB: number | undefined,
  sortDirection: SortDirectionEnum = SortDirectionEnum.Ascending
): number => {
  if (valueA === undefined) {
    return valueB === undefined ? 0 : sortDirection;
  } else if (valueB === undefined) {
    return -sortDirection;
  }

  return valueA < valueB ? -sortDirection : valueA > valueB ? sortDirection : 0;
};

export const compareStrings = (
  valueA: string | undefined,
  valueB: string | undefined,
  sortDirection: SortDirectionEnum = SortDirectionEnum.Ascending
): number => {
  if (valueA === undefined) {
    return valueB === undefined ? 0 : sortDirection;
  } else if (valueB === undefined) {
    return -sortDirection;
  }

  return valueA.localeCompare(valueB) * sortDirection;
};

export const getCompareNumbersFunction = <TItem>(
  getOrder: (item: TItem) => number | undefined,
  sortDirection: SortDirectionEnum = SortDirectionEnum.Ascending
): ((itemA: TItem, itemB: TItem) => number) => {
  return (itemA, itemB) => compareNumbers(getOrder(itemA), getOrder(itemB), sortDirection);
};

export const getCompareStringsFunction = <TItem>(
  getOrder: (item: TItem) => string | undefined,
  sortDirection: SortDirectionEnum = SortDirectionEnum.Ascending
): ((itemA: TItem, itemB: TItem) => number) => {
  return (itemA, itemB) => compareStrings(getOrder(itemA), getOrder(itemB), sortDirection);
};

export const getCompareDatesFunction = <TItem>(
  getOrder: (item: TItem) => Date | undefined,
  sortDirection: SortDirectionEnum = SortDirectionEnum.Ascending
): ((itemA: TItem, itemB: TItem) => number) => {
  return (itemA, itemB) => compareDates(getOrder(itemA), getOrder(itemB), sortDirection);
};

export const compareDates = (
  dateA: Date | undefined,
  dateB: Date | undefined,
  sortDirection: SortDirectionEnum
): number => {
  // Handle undefined values
  if (dateA === undefined && dateB === undefined) return 0;
  if (dateA === undefined) return sortDirection === SortDirectionEnum.Ascending ? 1 : -1;
  if (dateB === undefined) return sortDirection === SortDirectionEnum.Ascending ? -1 : 1;

  // Compare the date values
  const timeA = dateA.getTime();
  const timeB = dateB.getTime();

  return sortDirection === SortDirectionEnum.Ascending ? timeA - timeB : timeB - timeA;
};

class Group<TIem, TGroupBy> {
  key: TGroupBy;
  members: TIem[] = [];
  constructor(key: TGroupBy) {
    this.key = key;
  }
}

export function groupBy<TItem, TGroupBy>(list: TItem[], func: (x: TItem) => TGroupBy): Group<TItem, TGroupBy>[] {
  const res: Group<TItem, TGroupBy>[] = [];
  let group: Group<TItem, TGroupBy> | null = null;

  // Sort by the grouping property first to ensure items with the same key are contiguous
  const sortedList = [...list].sort((a, b) => {
    const keyA = func(a);
    const keyB = func(b);

    // Handle comparison for different types
    if (keyA === keyB) return 0;
    if (keyA === null || keyA === undefined) return 1;
    if (keyB === null || keyB === undefined) return -1;

    // String comparison
    if (typeof keyA === "string" && typeof keyB === "string") {
      return keyA.localeCompare(keyB);
    }

    // Number comparison
    if (typeof keyA === "number" && typeof keyB === "number") {
      return keyA - keyB;
    }

    // Generic comparison
    return keyA < keyB ? -1 : 1;
  });

  sortedList.forEach((o) => {
    const groupBy = func(o);

    if (group === null) {
      group = new Group<TItem, TGroupBy>(groupBy);
    }

    if (groupBy !== group.key) {
      res.push(group);
      group = new Group<TItem, TGroupBy>(groupBy);
    }

    group.members.push(o);
  });

  if (group != null) {
    res.push(group);
  }

  return res;
}

export function onlyUnique(value: string, index: number, array: string[]) {
  return array.indexOf(value) === index;
}

export function remove<T>(arr: T[], predicate: (item: T) => boolean): T[] {
  let i = arr.length;
  while (i--) {
    if (predicate(arr[i])) {
      arr.splice(i, 1);
    }
  }
  return arr;
}

export function concat<T>(...args: T[][]): T[] {
  return args.reduce((acc, curr) => acc.concat(curr), []);
}
